import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useCompany } from "../../contexts/CompanyContext";
import { useAuth } from "../../contexts/AuthContext";
import { useRole, ALL_NAV_ITEMS } from "../../contexts/RoleContext";

function getRankInfo(rep: number): { label: string; color: string; bg: string; border: string } {
  if (rep >= 81) return { label: "팀장", color: "#EAB308", bg: "#EAB30815", border: "#EAB30830" };
  if (rep >= 61) return { label: "과장", color: "#E8375F", bg: "#E8375F15", border: "#E8375F30" };
  if (rep >= 41) return { label: "대리", color: "#FF9F43", bg: "#FF9F4315", border: "#FF9F4330" };
  if (rep >= 21) return { label: "사원", color: "#7A9BFF", bg: "#4B6CF715", border: "#4B6CF730" };
  return { label: "인턴", color: "#999999", bg: "#99999915", border: "#99999930" };
}
import NPCChatModal from "./NPCChatModal";
export default function Sidebar() {
  const navigate = useNavigate();
  const { companyState } = useCompany();
  const { profile, signOut } = useAuth();
  const { jobRole, roleConfig, roleConfigs, companyConfig, unreadCount, isRoleLocked } = useRole();
  const [npcOpen, setNpcOpen] = useState(false);

  const capital = companyState?.capital ?? 0;
  const reputation = companyState?.reputation ?? 0;

  const allowedKeys = roleConfig.mainNav;
  const allCompanyKeys = Array.from(new Set(roleConfigs.flatMap(r => r.mainNav)));
  const navKeys    = allCompanyKeys.filter(k => allowedKeys.includes(k));
  const lockedKeys = allCompanyKeys.filter(k => !allowedKeys.includes(k) && k !== 'dashboard');

  return (
    <>
    <aside className="w-56 min-h-screen bg-white flex flex-col border-r border-black/[0.08] fixed left-0 top-0 z-40">
      {/* Logo */}
      <button
        type="button"
        className="flex items-center gap-2.5 px-5 py-5 border-b border-black/[0.07] cursor-pointer hover:bg-black/[0.02] transition-colors text-left w-full"
        onClick={() => navigate("/dashboard")}
      >
        <img
          src="https://storage.readdy-site.link/project_files/e81f0f74-7f28-46c8-8485-3df778381a50/9a17726c-ea2b-4ef1-b154-44e1a8dd8821_Gemini_Generated_Image_at6qf4at6qf4at6q-removebg-preview.png?v=7e9fc5a9989fd2f2c3e90b758ae037ba"
          alt="K-ArtPEN 로고"
          className="w-7 h-7 object-contain"
        />
        <div>
          <div className="text-[#111111] font-semibold text-sm tracking-wide leading-tight">K-ArtPEN</div>
          <div className="text-[#BBBBBB] text-[9px] tracking-widest uppercase">{companyConfig.labelEn}</div>
        </div>
      </button>

      {/* Company + Role Badge */}
      <div className="mx-3 mt-3 mb-1 space-y-1.5">
        <button
          type="button"
          onClick={() => navigate('/company-select')}
          className="w-full px-3 py-2 rounded-lg bg-[#4B6CF7]/[0.07] border border-[#4B6CF7]/15 flex items-center gap-2 hover:bg-[#4B6CF7]/[0.12] transition-colors cursor-pointer"
        >
          <div className="w-4 h-4 flex items-center justify-center shrink-0">
            <i className={`${companyConfig.icon} text-xs text-[#4B6CF7]/70`}></i>
          </div>
          <div className="flex-1 min-w-0 text-left">
            <div className="text-[#4B6CF7]/80 text-[10px] font-medium truncate">{companyConfig.label}</div>
          </div>
          <i className="ri-arrow-right-s-line text-[#4B6CF7]/40 text-xs shrink-0"></i>
        </button>

        <div className="px-3 py-2 rounded-lg bg-black/[0.03] border border-black/[0.07] flex items-center gap-2">
          <div className="w-4 h-4 flex items-center justify-center shrink-0">
            <i className={`${roleConfig.icon} text-xs text-[#666666]`}></i>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[#444444] text-[11px] font-medium truncate">{roleConfig.label.split('/')[0].trim()}</div>
          </div>
          {isRoleLocked ? (
            <div
              className="w-4 h-4 flex items-center justify-center text-[#DDDDDD] shrink-0"
              title="직무 변경은 관리자에게 문의하세요"
            >
              <i className="ri-lock-line text-xs"></i>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => navigate('/role-select')}
              className="w-4 h-4 flex items-center justify-center text-[#BBBBBB] hover:text-[#777777] transition-colors cursor-pointer shrink-0"
              title="직무 변경"
            >
              <i className="ri-refresh-line text-xs"></i>
            </button>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="px-3 pt-2 flex-1 overflow-y-auto">
        <div className="space-y-0.5">
          {navKeys.map((key) => {
            const item = ALL_NAV_ITEMS[key];
            if (!item) return null;
            const showBadge = key === 'inbox' && unreadCount > 0;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all whitespace-nowrap ${
                    isActive
                      ? "bg-[#4B6CF7]/10 text-[#4B6CF7]"
                      : "text-[#888888] hover:text-[#333333] hover:bg-black/[0.04]"
                  }`
                }
              >
                <div className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className={`${item.icon} text-sm`}></i>
                </div>
                <span className="flex-1 text-[13px]">{item.label}</span>
                {showBadge && (
                  <span className="bg-[#4B6CF7]/15 text-[#4B6CF7] text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                    {unreadCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </div>

        {lockedKeys.length > 0 && jobRole !== 'CEO' && (
          <>
            <div className="border-t border-black/[0.06] mt-3 mb-2 pt-3">
              <div className="text-[#CCCCCC] text-[9px] uppercase tracking-widest px-3 mb-1.5">다른 부서</div>
            </div>
            {lockedKeys.map((key) => {
              const item = ALL_NAV_ITEMS[key];
              if (!item) return null;
              return (
                <div key={key} className="flex items-center gap-2.5 px-3 py-2 rounded-lg opacity-30 cursor-not-allowed">
                  <div className="w-4 h-4 flex items-center justify-center shrink-0">
                    <i className={`${item.icon} text-sm text-[#999999]`}></i>
                  </div>
                  <span className="flex-1 text-[13px] text-[#999999]">{item.label}</span>
                  <i className="ri-lock-line text-[10px] text-[#BBBBBB]"></i>
                </div>
              );
            })}
          </>
        )}
      </nav>

      {/* Company Stats */}
      <div className="mx-3 mb-3">
        <div className="bg-[#F5F5F5] border border-black/[0.07] rounded-xl p-3 space-y-2.5">
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-[#BBBBBB] text-[10px]">{companyConfig.isNonProfit ? '보조금 잔액' : '자본금'}</span>
              <span className="text-[#555555] text-[11px] font-medium tabular-nums">
                {capital.toLocaleString()}만원
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#BBBBBB] text-[10px]">{companyConfig.isNonProfit ? '사회공헌 지수' : '명성'}</span>
              <span className="text-[#555555] text-[11px] font-medium">{reputation} / 100</span>
            </div>
          </div>
          <div className="w-full bg-black/[0.06] rounded-full h-px">
            <div
              className="h-px rounded-full bg-[#4B6CF7]/60 transition-all duration-500"
              style={{ width: `${reputation}%` }}
            />
          </div>
        </div>
      </div>

      {/* AI NPC Button */}
      <div className="mx-3 mb-2">
        <button
          type="button"
          onClick={() => setNpcOpen(true)}
          className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-gradient-to-r from-[#D97706]/10 to-[#EA580C]/10 border border-[#D97706]/20 hover:from-[#D97706]/18 hover:to-[#EA580C]/18 transition-all cursor-pointer"
        >
          <div className="w-6 h-6 flex items-center justify-center shrink-0">
            <i className="ri-robot-2-line text-[#D97706] text-sm"></i>
          </div>
          <div className="flex-1 text-left min-w-0">
            <div className="text-[#D97706] text-[11px] font-semibold leading-tight">AI NPC 어드바이저</div>
            <div className="text-[#BBBBBB] text-[9px]">기자·투자자·멘토 등 6인</div>
          </div>
          <span className="text-[9px] bg-[#D97706] text-white px-1.5 py-0.5 rounded-full font-bold shrink-0">AI</span>
        </button>
      </div>

      {/* Portfolio Button */}
      <div className="mx-3 mb-2">
        <NavLink
          to="/portfolio"
          className={({ isActive }) =>
            `w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition-all cursor-pointer ${
              isActive
                ? "bg-[#00C896]/15 border-[#00C896]/30"
                : "bg-[#00C896]/[0.04] border-[#00C896]/10 hover:bg-[#00C896]/10"
            }`
          }
        >
          <div className="w-6 h-6 flex items-center justify-center shrink-0">
            <i className="ri-user-star-line text-[#00C896]/80 text-sm"></i>
          </div>
          <div className="flex-1 text-left min-w-0">
            <div className="text-[#00C896]/80 text-[11px] font-semibold leading-tight">내 성과 포트폴리오</div>
            <div className="text-[#BBBBBB] text-[9px]">활동 자동 정리 카드</div>
          </div>
        </NavLink>
      </div>

      {/* Project Board Button */}
      <div className="mx-3 mb-2">
        <NavLink
          to="/project-board"
          className={({ isActive }) =>
            `w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition-all cursor-pointer ${
              isActive
                ? "bg-[#4B6CF7]/15 border-[#4B6CF7]/30"
                : "bg-[#4B6CF7]/[0.04] border-[#4B6CF7]/10 hover:bg-[#4B6CF7]/10"
            }`
          }
        >
          <div className="w-6 h-6 flex items-center justify-center shrink-0">
            <i className="ri-kanban-view-2 text-[#7A9BFF]/80 text-sm"></i>
          </div>
          <div className="flex-1 text-left min-w-0">
            <div className="text-[#7A9BFF]/80 text-[11px] font-semibold leading-tight">팀 프로젝트 보드</div>
            <div className="text-[#BBBBBB] text-[9px]">칸반 공유 협업</div>
          </div>
        </NavLink>
      </div>

      {/* Etiquette Guide Button */}
      <div className="mx-3 mb-2">
        <NavLink
          to="/etiquette"
          className={({ isActive }) =>
            `w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition-all cursor-pointer ${
              isActive
                ? "bg-amber-500/15 border-amber-500/30"
                : "bg-amber-500/[0.06] border-amber-500/15 hover:bg-amber-500/12"
            }`
          }
        >
          <div className="w-6 h-6 flex items-center justify-center shrink-0">
            <i className="ri-medal-line text-amber-400/80 text-sm"></i>
          </div>
          <div className="flex-1 text-left min-w-0">
            <div className="text-amber-400/80 text-[11px] font-semibold leading-tight">비즈니스 예절 가이드</div>
            <div className="text-[#BBBBBB] text-[9px]">이메일·회의·보고 예절 {18}가지</div>
          </div>
          <span className="text-[9px] bg-amber-500/20 text-amber-400/80 px-1.5 py-0.5 rounded-full font-bold shrink-0 border border-amber-500/20">NEW</span>
        </NavLink>
      </div>

      {/* Mini Employee ID Card */}
      <div className="mx-3 mb-3 border-t border-black/[0.07] pt-3">
        <button
          type="button"
          onClick={() => navigate("/profile")}
          className="w-full text-left group"
          title="프로필 수정"
        >
          <div className="rounded-xl overflow-hidden border border-black/[0.08] group-hover:border-[#4B6CF7]/30 transition-all"
            style={{ background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 80%, #0f3460 100%)" }}>
            {/* 상단 컬러 라인 */}
            <div className="h-0.5" style={{ background: "linear-gradient(90deg, #4B6CF7, #7A9BFF, #4B6CF7)" }} />
            <div className="p-3 flex items-center gap-2.5">
              {/* 사진 */}
              <div className="w-10 h-12 rounded-lg overflow-hidden shrink-0 border border-white/10"
                style={{ background: "linear-gradient(135deg, #252830, #1C1F2E)" }}>
                {profile?.avatar_url ? (
                  <img src={profile.avatar_url} alt="프로필" className="w-full h-full object-cover object-top" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-user-3-line text-white/20 text-base"></i>
                  </div>
                )}
              </div>
              {/* 정보 */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="text-white text-[11px] font-semibold truncate">
                    {profile?.display_name ?? "이름 없음"}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <span
                    className="text-[8px] font-bold px-1.5 py-0.5 rounded-full border"
                    style={{
                      color: getRankInfo(reputation).color,
                      backgroundColor: getRankInfo(reputation).bg,
                      borderColor: getRankInfo(reputation).border,
                    }}
                  >
                    {getRankInfo(reputation).label}
                  </span>
                  <span className="text-white/30 text-[9px] truncate">{roleConfig.label.split('/')[0].trim()}</span>
                </div>
                {/* 직급 프로그레스 바 */}
                <div className="w-full h-1 bg-white/[0.08] rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${reputation}%`,
                      backgroundColor: getRankInfo(reputation).color,
                      opacity: 0.6,
                    }}
                  />
                </div>
                <div className="text-white/20 text-[8px] mt-0.5">{reputation}pt</div>
              </div>
              {/* 편집 아이콘 */}
              <i className="ri-edit-2-line text-white/20 group-hover:text-white/50 text-xs transition-colors shrink-0"></i>
            </div>
          </div>
        </button>

        {/* 하단 액션 버튼 */}
        <div className="flex items-center justify-between mt-2 px-1">
          {profile?.role === 'admin' && (
            <NavLink to="/admin" className="flex items-center gap-1 text-[#CCCCCC] hover:text-[#777777] transition-colors cursor-pointer text-[10px]" title="관리자 콘솔">
              <i className="ri-settings-3-line text-xs"></i>
              <span>관리자</span>
            </NavLink>
          )}
          <button type="button" onClick={signOut}
            className={`flex items-center gap-1 text-[#CCCCCC] hover:text-[#777777] transition-colors cursor-pointer text-[10px] ${profile?.role !== 'admin' ? 'ml-auto' : ''}`}
            title="로그아웃">
            <i className="ri-logout-box-r-line text-xs"></i>
            <span>로그아웃</span>
          </button>
        </div>
      </div>
    </aside>

      <NPCChatModal isOpen={npcOpen} onClose={() => setNpcOpen(false)} />
    </>
  );
}
